import { Controller, type Control } from 'react-hook-form'
import Box from '@mui/material/Box'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import type { CompanyCard } from '@/features/companies/types'
import type { CampaignStep1FormValues } from '../lib/campaign-schema'

const FIELD_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    bgcolor: '#FFF',
  },
  '& .MuiOutlinedInput-root:not(.Mui-focused):not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
    borderColor: '#E5E7EB',
  },
  '& .MuiOutlinedInput-root:hover:not(.Mui-disabled):not(.Mui-focused):not(.Mui-error) .MuiOutlinedInput-notchedOutline':
    {
      borderColor: '#E5E7EB',
    },
} as const

interface Props {
  control: Control<CampaignStep1FormValues>
  options: CompanyCard[]
  locked: boolean
  fallbackName?: string
}

/** BFP-1010 — optional Company select below Target Audience */
export function CampaignSetupCompanyField({
  control,
  options,
  locked,
  fallbackName,
}: Readonly<Props>) {
  return (
    <Box data-testid="campaign-setup-company-field">
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
        <Typography component="label" variant="body2" fontWeight={500}>
          Company
        </Typography>
        <Typography component="span" variant="body2" color="text.secondary">
          (Optional)
        </Typography>
      </Stack>
      <Controller
        name="companyId"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            select
            fullWidth
            value={field.value ?? ''}
            disabled={locked}
            sx={FIELD_SX}
            SelectProps={{
              displayEmpty: true,
              renderValue: (selected) => {
                const id = typeof selected === 'string' ? selected : ''
                if (!id) {
                  return (
                    <Typography component="span" color="text.secondary">
                      Select a company
                    </Typography>
                  )
                }
                const match = options.find((c) => c.id === id)
                return match?.name ?? fallbackName ?? id
              },
            }}
          >
            <MenuItem value="">
              <em>Select a company</em>
            </MenuItem>
            {options.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
        )}
      />
    </Box>
  )
}
